import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ОБЛАЧКА СПОР — эффект грибной подложки (mushroom_1).
 *
 * Грибница выдыхает: колония на миг разгорается, из нее в стороны вылетает
 * рой светящихся пылинок — и облачко медленно плывет по ветру через поле,
 * расползаясь и мерцая, пока не растает (см. utils/effects/biome/sporePuffs).
 *
 * Выдыхают и колонии, нарисованные на пейзаже кольцом вокруг поля, и грибы на
 * самом поле: жетон горы на этой подложке нарисован грибом (тайл
 * mountain_mushroom). Споры не возникают из ниоткуда — у каждого облачка есть
 * источник: колония на картинке пейзажа либо шляпка гриба-горы. Облачко
 * колонии — цвета ее свечения.
 *
 * Чем облачко НЕ должно стать: цветным снегом. От снега, пепла и углей его
 * отличают вспышка у источника, холодные светящиеся цвета, медлительность и
 * мерцание — без выброса это просто летящие точки.
 */
export const SPORE_PUFFS_KIND = 'sporePuffs';

// Три цвета свечения грибов на картинке пейзажа
const TEAL = 'teal';
const LAVENDER = 'lavender';
const LIME = 'lime';

/**
 * ПЫЛИНКА — одна и та же у облачка колонии и у облачка гриба-горы: это одни
 * споры. Низко над полем — над пустыми ячейками и жетонами строений и гор, но
 * под отрядами и их значками: облачка плывут через поле все время, над отрядами
 * они мешали бы их читать.
 *
 * Пылинки МЕЛКИЕ, и их МНОГО — так решил владелец игры, посмотрев эффект в игре:
 * прежние (вдвое крупнее и вдвое реже) читались горстью светящихся шариков, а
 * не роем спор.
 */
const MOTE_LOOK = Object.freeze({
  layer: 'LOW_AIR_GLOW',
  // Кадр пятна в размерах ячейки; яркая середина — около трети кадра: на
  // компьютере это 5–8 точек экрана, на телефоне — 2–3. Одну такую на телефоне
  // едва видно, но пылинка не одна: читается рой
  sizeFactor: {min: 0.3, max: 0.45},
  alpha: 0.9,
  // Над игровыми ячейками пылинки чуть тусклее: под ними строения
  overFieldAlphaShare: 0.9,
  // Мерцание: яркость падает на эту долю и возвращается
  twinkleDepth: 0.4,
  twinklePeriodMs: {min: 1300, max: 2400},
  // Пылинка покачивается на месте: один круг — за столько
  bobPeriodMs: {min: 3000, max: 6000},
  // К концу жизни пылинка крупнее во столько раз: поднимается к зрителю
  riseScale: 1.25,
});

export const SPORE_PUFFS_EFFECT = Object.freeze({
  kind: SPORE_PUFFS_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.MUSHROOM_1]: Object.freeze({
      firstDelayMs: {min: 1500, max: 4000},
      // Выдох — раз в несколько секунд; пауза — от начала предыдущего. Облачко
      // живет полминуты и дольше, так что плывет их разом до maxGroups, а над
      // самим полем — два-три: остальные еще на подлете или уже за полем
      intervalMs: {min: 5500, max: 8500},
      maxGroups: 5,
      // Поле не начинается пустым: столько облачков уже в пути к началу партии
      prewarm: 3,
      // КАКАЯ КОЛОНИЯ ВЫДОХНЕТ
      emission: {
        // Чаще выдыхают колонии, от которых споры доплывут до поля. Вес колонии —
        // косинус угла, на который ее курс к середине поля не укладывается в
        // допустимый наклон от ветра (drift.leanDeg), но не меньше этого:
        // изредка дышат и колонии с подветренной стороны
        minWindWeight: 0.15,
        // Колония, чье облачко пройдет через то, что видит камера, выбирается
        // во столько раз чаще
        inViewBoost: 6,
      },
      // ВСПЫШКА КОЛОНИИ: мягкое пятно на земле, под жетонами
      glow: {
        layer: 'GROUND_GLOW',
        // Колония разгорается, и через leadMs из нее вылетают споры
        leadMs: 450,
        riseMs: 700,
        fallMs: 2600,
        alpha: 0.3,
        // Пятно шире нарисованной колонии во столько раз (кадр пятна; видимое
        // свечение — около его половины)
        scale: 2.6,
      },
      // ПЫЛИНКИ ОБЛАЧКА КОЛОНИИ
      motes: {
        ...MOTE_LOOK,
        // Рой, а не горсть: пылинки мелкие, и меньшим числом облачко не собрать
        count: {min: 20, max: 24},
        // Проступают на вылете: все разом в одной точке они дали бы белую вспышку
        fadeInMs: 700,
        // Пылинки тают не разом: каждая — в свой миг последних staggerMs жизни
        // облачка, за fadeOutMs
        staggerMs: 8000,
        fadeOutMs: 2500,
        // Размах покачивания в размерах ячейки
        bobFactor: {min: 0.24, max: 0.4},
      },
      // ВЫБРОС: пылинки разлетаются от колонии по лучам и замирают
      burst: {
        durationMs: 1800,
        // На какое расстояние от колонии, размеров ячейки (ячейка — два размера).
        // Пылинки ложатся по всему кругу между этими расстояниями, а не кольцом
        radiusFactor: {min: 0.35, max: 1.8},
      },
      // ДРЕЙФ: облачко плывет по ветру матча, как облака, забирая к полю
      drift: {
        // Размеров ячейки в секунду: меньше половины ячейки — медленнее любого снега
        speedFactor: {min: 0.7, max: 0.9},
        // Колонии стоят кольцом вокруг поля, а ветер дует вдоль одной оси:
        // строго по ветру через поле прошли бы споры двух-трех колоний. Поэтому
        // облачко забирает от ветра в сторону поля — не больше чем на этот угол
        leanDeg: 32,
        // Цель облачка — игровая ячейка в кадре или рядом с ним: кадр берется с
        // запасом в столько размеров ячейки, чтобы при крупном плане облачка не
        // сходились все в одно место
        targetViewMarginFactor: 2,
        // Разгоняется не сразу: сначала выброс
        rampMs: 2500,
        // Облачко плывет не по линейке: виляет поперек ветра на столько
        // размеров ячейки (курс гуляет градусов на двенадцать)
        weaveFactor: 0.9,
        weavePeriodMs: 40000,
        // Сколько живет облачко: доплывает до своей ячейки-цели и еще такую
        // долю оставшегося пути до дальнего края поля, но в пределах lifeMs.
        // Чем дальше колония от поля, тем дольше живет ее облачко, — до
        // потолка lifeMs.max: он держит число облачков в воздухе в меру. На
        // очень большом поле (экспериментальная «Европа») колонии так далеки,
        // что облачка тают, не доплыв до середины поля
        reachShare: {min: 0.3, max: 0.9},
        lifeMs: {min: 20000, max: 52000},
        // За жизнь облачко расползается: к концу оно шире во столько раз
        growth: 2,
      },
      /**
       * ГРИБЫ-ГОРЫ. Жетон горы на этой подложке нарисован грибом, и время от
       * времени один из них выдыхает так же, как колония: шляпка вспыхивает, из
       * нее поднимается облачко тех же спор, расходится, отплывает по ветру на
       * ячейку-другую и тает. Облачко гриба меньше облачка большой колонии и
       * живет недолго: оно рождается прямо на поле, среди жетонов, и не должно
       * надолго закрывать соседние.
       *
       * Нет на поле гор — нет и этих облачков.
       */
      mountains: {
        firstDelayMs: {min: 3000, max: 7000},
        // Выдох гриба — раз в несколько секунд; пауза — от начала предыдущего
        intervalMs: {min: 5000, max: 9000},
        // Грибы выдыхают по одному, изредка двое разом — но не все поле сразу.
        // Гриб, чье облачко еще в воздухе, молчит, и дважды подряд один и тот же
        // не выдыхает, пока на поле есть другой
        maxGroups: 2,
        // Гриб, который видит камера, выбирается во столько раз чаще
        inViewBoost: 8,
        /**
         * Шляпка гриба на жетоне: сдвиг ее середины от центра ячейки в
         * размерах ячейки (вправо и вниз). Отсюда вспышка и отсюда же вылетают
         * споры.
         *
         * Сверено с картинкой жетона (mountain_mushroom, 830 на 630 точек):
         * шляпка занимает на ней точки 290–595 по горизонтали и 52–232 по
         * вертикали (это 1.17 на 0.69 размера ячейки), середина — (443, 142).
         * Игра рисует картинку в масштабе TOKEN_SCALE (0.23) серединой на 2
         * точки мира выше центра ячейки (MOUNTAIN_SHIFT_Y в HexGridRenderer),
         * размер ячейки — 60: точка картинки (px, py) — это сдвиг
         * ((px - 415) * 0.23, (py - 315) * 0.23 - 2) / 60. Сменится картинка
         * жетона или его масштаб — шляпку надо сверить заново.
         */
        cap: {dx: 0.11, dy: -0.7},
        // Шляпка на жетоне бирюзовая в лавандовых наростах: облачко гриба —
        // одного из этих двух цветов
        tints: [TEAL, LAVENDER],
        // ВСПЫШКА ШЛЯПКИ: мягкое свечение поверх жетона горы, под отрядами
        flash: {
          layer: 'LOW_AIR_GLOW',
          // Шляпка разгорается, и через leadMs из нее вылетают споры
          leadMs: 350,
          riseMs: 500,
          fallMs: 1900,
          alpha: 0.5,
          // Кадр пятна в размерах ячейки. Видимое свечение — около его
          // половины: загорается середина шляпки, края остаются темными
          sizeFactor: 1.4,
        },
        // ПЫЛИНКИ ОБЛАЧКА ГРИБА — те же, что у колонии, но их вдвое меньше
        motes: {
          ...MOTE_LOOK,
          count: {min: 9, max: 12},
          fadeInMs: 450,
          // Тают в последние секунды облачка — каждая в свой миг
          staggerMs: 2500,
          fadeOutMs: 1200,
          // Облачко маленькое — и покачивание в нем мельче
          bobFactor: {min: 0.1, max: 0.2},
        },
        // ВЫБРОС: втрое теснее, чем у колонии, — облачко в ячейку шириной
        burst: {
          durationMs: 1400,
          radiusFactor: {min: 0.12, max: 0.6},
        },
        // ПОДЪЕМ: облачко поднимается над шляпкой на столько размеров ячейки
        // (на поле мы смотрим сверху и чуть сбоку: вверх — это вверх по экрану)
        rise: {heightFactor: 0.45, durationMs: 2200},
        // ДРЕЙФ: строго по ветру матча — облачко и так уже на поле
        drift: {
          // Сколько облачко проплывает за жизнь, размеров ячейки: ячейку-другую
          // (ячейка — два размера) — и тает
          reachFactor: {min: 2, max: 4},
          lifeMs: {min: 6000, max: 9000},
          // Сначала подъем и выброс, разгон — следом
          rampMs: 1800,
          // К концу облачко шире во столько раз
          growth: 1.6,
        },
      },
      // Лавандовый гуще, чем шляпки на картинке: бледный на светящемся слое
      // выгорает в белый, и облачко читается снегом
      tints: {[TEAL]: 0x7ff0e6, [LAVENDER]: 0xb596ff, [LIME]: 0xc9f29a},
      // Колонии на картинке пейзажа (доли ее стороны), по кругу от левого
      // нижнего угла. weight — у колоний возле самого края пейзажа: там все
      // гаснет, и облачко рождалось бы тусклым
      colonies: [
        // Лавандовые шляпки с дымкой, большая колония ниже и левее поля
        {u: 0.332, v: 0.816, radiusU: 0.04, tint: LAVENDER},
        {u: 0.139, v: 0.715, radiusU: 0.035, tint: LAVENDER},
        {u: 0.104, v: 0.573, radiusU: 0.03, tint: LAVENDER, weight: 0.6},
        // Бирюзовое свечение слева
        {u: 0.124, v: 0.379, radiusU: 0.03, tint: TEAL},
        {u: 0.184, v: 0.28, radiusU: 0.03, tint: TEAL},
        // Сверху
        {u: 0.312, v: 0.128, radiusU: 0.025, tint: LAVENDER},
        {u: 0.431, v: 0.185, radiusU: 0.03, tint: TEAL},
        {u: 0.605, v: 0.19, radiusU: 0.025, tint: TEAL},
        {u: 0.686, v: 0.106, radiusU: 0.025, tint: LAVENDER, weight: 0.5},
        // Лаймовая колония у самого правого верхнего угла поля
        {u: 0.695, v: 0.265, radiusU: 0.025, tint: LIME},
        {u: 0.772, v: 0.202, radiusU: 0.03, tint: LAVENDER},
        // Справа
        {u: 0.868, v: 0.295, radiusU: 0.025, tint: LIME, weight: 0.5},
        {u: 0.875, v: 0.598, radiusU: 0.035, tint: LAVENDER, weight: 0.5},
        {u: 0.85, v: 0.76, radiusU: 0.02, tint: LIME},
        // Снизу
        {u: 0.746, v: 0.862, radiusU: 0.035, tint: TEAL},
        // Лавандовая гроздь у нижнего края поля
        {u: 0.616, v: 0.754, radiusU: 0.025, tint: LAVENDER},
        {u: 0.414, v: 0.847, radiusU: 0.03, tint: LIME},
        {u: 0.265, v: 0.845, radiusU: 0.025, tint: TEAL},
      ],
    }),
  }),
});
